import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material';

interface LogItem {
    fileName: string;
    runAt: string;
    customerCount: number;
    downloadPath: string;
}

const DailyAlerts: React.FC = () => {
    const [logs, setLogs] = useState<LogItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    const apiBase = React.useMemo(
        () => {
            const baseUrl = process.env.REACT_APP_API_URL || '';
            // If baseUrl already includes /api, use it as-is, otherwise add /api/
            if (baseUrl.includes('/api')) {
                return baseUrl.replace(/\/?$/, '/');
            } else if (baseUrl) {
                return baseUrl.replace(/\/?$/, '') + '/api/';
            } else {
                return '/api/';
            }
        },
        []
    );

    const fetchLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(`${apiBase}promotion-logs`, {
                params: {
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                },
            });
            console.log('[DailyAlerts] Fetched logs:', data.logs?.length || 0, 'logs');
            if (data.debug) {
                console.log('[DailyAlerts] Debug info:', data.debug);
            }
            setLogs(data.logs || []);
        } catch (err: any) {
            console.error('Failed to load promotion logs', err);
            console.error('Error details:', {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status,
                url: `${apiBase}promotion-logs`
            });
            setError(err.response?.data?.error || 'Failed to load promotion logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApply = () => {
        fetchLogs();
    };

    const formatDateTime = (iso: string) => {
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
    };

    return (
        <Box>
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader title="Daily Alerts Logs" subheader="Runs of sendPromoMessages.js" />
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                fullWidth
                                label="Start date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                fullWidth
                                label="End date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <Button variant="contained" onClick={handleApply}>
                                Apply filters
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardHeader title="Runs" />
                <CardContent>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Typography color="error">{error}</Typography>
                    ) : logs.length === 0 ? (
                        <Typography>No runs found for the selected range.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Run date</TableCell>
                                    <TableCell align="right">Customers sent</TableCell>
                                    <TableCell>Download log</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map((log) => (
                                    <TableRow key={log.fileName}>
                                        <TableCell>{formatDateTime(log.runAt)}</TableCell>
                                        <TableCell align="right">{log.customerCount}</TableCell>
                                        <TableCell>
                                            <Link
                                                href={`${apiBase}${log.downloadPath}`}
                                                target="_blank"
                                                rel="noopener"
                                            >
                                                Download
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default DailyAlerts;

